// `avatar autopilot <init|run|status|approve|pause|resume|answer|redirect|revert|abort|doctor|schema|capabilities|unlock|secrets-template|archive|resolve-deploy>` (17 lệnh con)
// (spec §3.3): init here (run: autopilot-run-command.ts, status: autopilot-status-command.ts); 7 inbox-only control commands live in autopilot-control-directive-commands.ts.
import { join } from "node:path";
import type { Command } from "commander";
import { registerAutopilotControlDirectiveCommands } from "../lib/autopilot/autopilot-control-directive-commands.js";
import { readUnusedConfigKeyWarnings } from "../lib/autopilot/config-keys-not-read-in-v1.js";
import {
  formatInitLeftoverHints,
  listMissionIdsForHint,
} from "../lib/autopilot/mission-archive.js";
import { initMission } from "../lib/autopilot/mission-initializer.js";
import { InitValidationError } from "../lib/autopilot/mission-initializer-errors.js";
import { writeMachineReadableJson } from "../lib/machine-readable-json-output-contract.js";
import { getActiveFlag, requireFlag } from "../lib/non-interactive-context.js";
import { AVATAR_HOME } from "../lib/user-config-store.js";
import { registerAutopilotArchiveCommand } from "./autopilot-archive-command.js";
import { registerAutopilotDoctorCommand } from "./autopilot-doctor-command.js";
import { registerAutopilotResolveDeployCommand } from "./autopilot-resolve-deploy-command.js";
import { registerAutopilotRunCommand } from "./autopilot-run-command.js";
import { registerAutopilotSecretsTemplateCommand } from "./autopilot-secrets-template-command.js";
import { registerAutopilotStatelessSubcommands } from "./autopilot-stateless-subcommands-registration.js";
import { registerAutopilotStatusCommand } from "./autopilot-status-command.js";
import { registerAutopilotUnlockCommand } from "./autopilot-unlock-command.js";

const DEFAULT_GLOBAL_CONFIG_PATH = join(AVATAR_HOME, "autopilot-config.json");
const DEFAULT_MACHINE_LOCK_PATH = join(AVATAR_HOME, "autopilot.lock");

async function resolveWorkspaceRoot(repoFlag: string | undefined): Promise<string> {
  const { resolveAvatarWorkspaceRootFromCwd } = await import(
    "../lib/resolve-avatar-workspace-root-from-cwd.js"
  );
  const root = resolveAvatarWorkspaceRootFromCwd(repoFlag ?? process.cwd());
  if (!root)
    throw new Error("Không tìm thấy Avatar workspace (thiếu .claude/avatar.json) từ cwd/--repo.");
  return root;
}

export function registerAutopilotCommand(program: Command): void {
  const autopilot = program
    .command("autopilot")
    .description("Chạy mission tự hành (Autopilot V2.1)");

  autopilot
    .command("init")
    .description("Khởi tạo mission từ request file (startbuild entry point)")
    .option("--request-file <path>", "Đường dẫn init request JSON")
    .action(async (opts: { requestFile?: string }) => {
      const requestFilePath = requireFlag(
        opts.requestFile ?? getActiveFlag<string>("requestFile"),
        {
          flag: "--request-file",
          command: "autopilot init",
          hint: "--request-file <path>",
        },
      );
      const workspaceRoot = await resolveWorkspaceRoot(undefined);
      const packSourceDir = join(workspaceRoot, ".claude", "pack");
      // Req 7: snapshot BEFORE so a failure below can name exactly which
      // mission dir `initMission` left behind (never log.*/stdout — that
      // channel is reserved for the JSON receipt of a SUCCESSFUL init).
      const before = await listMissionIdsForHint(workspaceRoot);
      try {
        const receipt = await initMission({
          requestFilePath,
          workspaceRoot,
          packSourceDir,
          actorOsAccount: process.env.USER ?? process.env.LOGNAME ?? "unknown",
          globalConfigPath: DEFAULT_GLOBAL_CONFIG_PATH,
        });
        // Req 3 (D7, C5): stderr, never the logger — --non-interactive stdout
        // stays exactly 1 JSON line (machine-readable-json-output-contract.ts).
        for (const w of await readUnusedConfigKeyWarnings(DEFAULT_GLOBAL_CONFIG_PATH)) {
          process.stderr.write(`⚠ ${w}\n`);
        }
        writeMachineReadableJson({ command: "autopilot init", receipt });
      } catch (err) {
        const after = await listMissionIdsForHint(workspaceRoot);
        for (const hint of formatInitLeftoverHints(before, after))
          process.stderr.write(`${hint}\n`);
        // Operator input error (config/base_sha/BLUEPRINT…), not a crash.
        if (err instanceof InitValidationError) {
          process.stderr.write(`✗ ${err.message}\n`);
          process.exit(1);
        }
        throw err;
      }
    });

  registerAutopilotRunCommand(autopilot, {
    resolveWorkspaceRoot,
    machineLockPath: DEFAULT_MACHINE_LOCK_PATH,
    globalConfigPath: DEFAULT_GLOBAL_CONFIG_PATH,
  });

  registerAutopilotStatusCommand(autopilot, {
    resolveWorkspaceRoot,
    machineLockPath: DEFAULT_MACHINE_LOCK_PATH,
  });

  const lockDeps = { resolveWorkspaceRoot, machineLockPath: DEFAULT_MACHINE_LOCK_PATH };
  registerAutopilotControlDirectiveCommands(autopilot, {
    ...lockDeps,
    globalConfigPath: DEFAULT_GLOBAL_CONFIG_PATH,
  });

  registerAutopilotDoctorCommand(autopilot, {
    resolveWorkspaceRoot,
    defaultGlobalConfigPath: DEFAULT_GLOBAL_CONFIG_PATH,
    machineLockPath: DEFAULT_MACHINE_LOCK_PATH,
  });

  // F2 `schema <name>` + V2 §7 `capabilities --json` (no mission state).
  registerAutopilotStatelessSubcommands(autopilot);

  registerAutopilotUnlockCommand(autopilot, lockDeps);
  registerAutopilotSecretsTemplateCommand(autopilot, { resolveWorkspaceRoot });
  registerAutopilotArchiveCommand(autopilot, lockDeps);
  // A5: exit from blocked:deploy_outcome_unknown other than archive.
  registerAutopilotResolveDeployCommand(autopilot, {
    ...lockDeps,
    globalConfigPath: DEFAULT_GLOBAL_CONFIG_PATH,
  });
}
